"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bucketsReduceItem = exports.bucketsModifyItem = exports.bucketsIsEmpty = exports.bucketsTotals = exports.bucketsItems = exports.bucketsById = exports.bucketsFindMeasure = exports.bucketsFindAttribute = exports.bucketsFind = exports.bucketsMeasures = exports.bucketsAttributes = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2020 GoodData Corporation
var index_1 = require("./index");
var attribute_1 = require("../attribute");
var measure_1 = require("../measure");
var flatMap_1 = tslib_1.__importDefault(require("lodash/flatMap"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var findIndex_1 = tslib_1.__importDefault(require("lodash/findIndex"));
/**
 * Gets all attributes matching the provided predicate from a list of buckets.
 *
 * If no predicate is provided, then the function defaults to {@link anyAttribute} predicate - meaning all
 * attributes will be returned.
 *
 * @param buckets - list of buckets to get attributes from
 * @param predicate - attribute predicate; {@link anyAttribute} is default
 * @returns empty list if none match
 * @public
 */
function bucketsAttributes(buckets, predicate) {
    if (predicate === void 0) { predicate = attribute_1.anyAttribute; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    if (!buckets.length) {
        return [];
    }
    return flatMap_1.default(buckets, function (b) { return index_1.bucketAttributes(b, predicate); });
}
exports.bucketsAttributes = bucketsAttributes;
/**
 * Gets all measures matching the provided predicate from a list of buckets.
 *
 * If no predicate is provided, then the function defaults to {@link anyMeasure} predicate - meaning all
 * measures will be returned.
 *
 * @param buckets - list of buckets to get measures from
 * @param predicate - measure predicate; {@link anyMeasure} is default
 * @returns empty list if none match
 * @public
 */
function bucketsMeasures(buckets, predicate) {
    if (predicate === void 0) { predicate = measure_1.anyMeasure; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    if (!buckets.length) {
        return [];
    }
    return flatMap_1.default(buckets, function (b) { return index_1.bucketMeasures(b, predicate); });
}
exports.bucketsMeasures = bucketsMeasures;
/**
 * Finds bucket matching the provided predicate in a list of buckets.
 *
 * If no predicate is provided, then the function defaults to {@link anyBucket} predicate - meaning first
 * bucket in the list will be returned.
 *
 * This function also provides convenience to find bucket by local identifier - if you pass predicate as
 * string the function will automatically create idMatchBucket predicate.
 *
 * @param buckets - list of buckets to search
 * @param idOrFun - bucket predicate or string to match bucket by local identifier; {@link anyBucket} is default
 * @public
 */
function bucketsFind(buckets, idOrFun) {
    if (idOrFun === void 0) { idOrFun = index_1.anyBucket; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    var predicate = typeof idOrFun === "string" ? index_1.idMatchBucket(idOrFun) : idOrFun;
    return buckets.find(predicate);
}
exports.bucketsFind = bucketsFind;
/**
 * Finds attribute matching the provided predicate in a list of buckets. If found, the function returns an object
 * that contains bucket where the matched attribute is stored, index within that bucket and the attribute itself.
 *
 * This function also provides convenience to find attribute by local identifier - if you pass predicate as
 * string the function will automatically create idMatchAttribute predicate.
 *
 * @remarks See {@link AttributeInBucket}
 *
 * @param buckets - list of buckets to search
 * @param idOrFun - attribute predicate or string to find attribute by local identifier; defaults to {@link anyAttribute}
 * @returns first-found attribute matching the predicate, undefined if none match
 * @public
 */
function bucketsFindAttribute(buckets, idOrFun) {
    if (idOrFun === void 0) { idOrFun = attribute_1.anyAttribute; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    if (!buckets.length) {
        return;
    }
    var predicate = typeof idOrFun === "string" ? attribute_1.idMatchAttribute(idOrFun) : idOrFun;
    var typeAgnosticPredicate = function (obj) {
        return attribute_1.isAttribute(obj) && predicate(obj);
    };
    for (var _i = 0, buckets_1 = buckets; _i < buckets_1.length; _i++) {
        var bucket = buckets_1[_i];
        var idx = findIndex_1.default(bucket.items, typeAgnosticPredicate);
        if (idx >= 0) {
            var item = bucket.items[idx];
            return attribute_1.isAttribute(item) ? { bucket: bucket, idx: idx, attribute: item } : undefined;
        }
    }
    return undefined;
}
exports.bucketsFindAttribute = bucketsFindAttribute;
/**
 * Finds measure matching the provided predicate in a list of buckets. If found, the function returns an object
 * that contains bucket where the matched measure is stored, index within that bucket and the measure itself.
 *
 * This function also provides convenience to find measure by local identifier - if you pass predicate as
 * string the function will automatically create idMatchMeasure predicate.
 *
 * @remarks See {@link MeasureInBucket}
 *
 * @param buckets - list of buckets to search
 * @param idOrFun - measure predicate or string to find measure by local identifier; defaults to {@link anyMeasure}
 * @returns first-found measure matching the predicate, undefined if none match
 * @public
 */
function bucketsFindMeasure(buckets, idOrFun) {
    if (idOrFun === void 0) { idOrFun = measure_1.anyMeasure; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    if (!buckets.length) {
        return;
    }
    var predicate = typeof idOrFun === "string" ? measure_1.idMatchMeasure(idOrFun) : idOrFun;
    var typeAgnosticPredicate = function (obj) {
        return measure_1.isMeasure(obj) && predicate(obj);
    };
    for (var _i = 0, buckets_2 = buckets; _i < buckets_2.length; _i++) {
        var bucket = buckets_2[_i];
        var idx = findIndex_1.default(bucket.items, typeAgnosticPredicate);
        if (idx >= 0) {
            var item = bucket.items[idx];
            return measure_1.isMeasure(item) ? { bucket: bucket, idx: idx, measure: item } : undefined;
        }
    }
    return undefined;
}
exports.bucketsFindMeasure = bucketsFindMeasure;
/**
 * Gets buckets with the provided local identifiers from a list of buckets.
 *
 * @param buckets - list of buckets to filter
 * @param ids - bucket identifiers
 * @returns empty list if none match
 * @public
 */
function bucketsById(buckets) {
    var ids = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        ids[_i - 1] = arguments[_i];
    }
    ts_invariant_1.default(buckets, "buckets must be specified");
    if (!buckets.length || !ids || !ids.length) {
        return [];
    }
    return buckets.filter(function (b) { return b.localIdentifier && ids.indexOf(b.localIdentifier) >= 0; });
}
exports.bucketsById = bucketsById;
/**
 * Gets all attributes and measures from a list of buckets.
 *
 * @param buckets - buckets to work with
 * @returns empty list if none
 * @public
 */
function bucketsItems(buckets) {
    ts_invariant_1.default(buckets, "buckets must be specified");
    return flatMap_1.default(buckets, function (b) { return index_1.bucketItems(b); });
}
exports.bucketsItems = bucketsItems;
/**
 * Gets all totals from a list of buckets
 *
 * @param buckets - buckets to work with
 * @returns empty list if none
 * @public
 */
function bucketsTotals(buckets) {
    ts_invariant_1.default(buckets, "buckets must be specified");
    return flatMap_1.default(buckets, function (b) { return index_1.bucketTotals(b); });
}
exports.bucketsTotals = bucketsTotals;
/**
 * Tests whether all buckets in a list are empty (meaning neither has any items or totals defined)
 *
 * @param buckets - buckets to work with
 * @returns true if empty, false if not
 * @public
 */
function bucketsIsEmpty(buckets) {
    ts_invariant_1.default(buckets, "buckets must be specified");
    if (!buckets.length) {
        return true;
    }
    return buckets.every(index_1.bucketIsEmpty);
}
exports.bucketsIsEmpty = bucketsIsEmpty;
/**
 * Creates a new array of buckets, each bucket in the array contains modified bucket items
 * (retrieved by applying the modifications function to the bucketItem).
 *
 * @param buckets - an array of buckets, all items of each bucket in the array are applied the modification function
 * @param modifications - the modification to apply to the bucket items
 * @returns a new array of buckets, each bucket in the array contains modified bucket items
 * @public
 */
function bucketsModifyItem(buckets, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    return buckets.map(function (bucket) { return index_1.bucketModifyItems(bucket, modifications); });
}
exports.bucketsModifyItem = bucketsModifyItem;
/**
 * Creates a new array of buckets, each bucket in the array contains modified bucket items
 *
 * @param buckets - an array of buckets, array is applied the modification function
 * @param reducer - the reducer to apply to the bucket items array
 * @returns a new array of buckets, each bucket in the array contains modified bucket items
 * @public
 */
function bucketsReduceItem(buckets, reducer) {
    if (reducer === void 0) { reducer = identity_1.default; }
    ts_invariant_1.default(buckets, "buckets must be specified");
    return buckets.map(function (bucket) { return index_1.bucketItemReduce(bucket, reducer); });
}
exports.bucketsReduceItem = bucketsReduceItem;
//# sourceMappingURL=bucketArray.js.map